import { Payments } from '@getgreenline/payments'
import { ExternalLoyaltyAPI, ExternalLoyaltyModels } from '@getgreenline/external-loyalty'
import { IPaymentQueue } from '@getgreenline/homi-shared'
import Endpoints from '../constants/Endpoints'
import { parseErrorMsg, buildMerchantId } from './helpers'
import type { CurrentCompanyStore } from '../stores/CurrentCompanyStore'
import type { POSStore } from '../stores/POSStore'

export type RewardLine = {
  externalReward?: { reservationId: string } | null
}

export type ReservedPaymentQueue = Pick<IPaymentQueue, 'customerId' | 'paymentLines'> & {
  locationId: number
}

export const reserveRewardPoints = async ({
  payment,
  companyId,
  locationId,
  rewardId,
}: {
  payment: Payments.CreatePaymentV2
  companyId: number
  locationId: number
  rewardId: string
}): Promise<{ reservationId: string } | { errorMessage: string }> => {
  const customerId = payment.customer?.id

  if (!customerId) {
    return { errorMessage: 'A customer must be attached to redeem this reward.' }
  }

  try {
    const reservation = await ExternalLoyaltyAPI.createPointsReservation({
      gatewayUrl: Endpoints.PLATFORM_GATEWAY_URL,
      merchantId: buildMerchantId(companyId, locationId),
      customerId,
      rewardId,
      sourceOrderId: payment.offlinePaymentId,
    })

    return { reservationId: reservation.reservationId }
  } catch (error) {
    return {
      errorMessage: parseErrorMsg(error) || 'Failed to reserve loyalty points for this reward',
    }
  }
}

export const maybeRefundRewardReservation = async ({
  customerId,
  companyId,
  locationId,
  reservationId,
}: {
  customerId?: string | null
  companyId: number
  locationId: number
  reservationId: string
}): Promise<{ errorMessage?: string }> => {
  if (!customerId) {
    return {}
  }

  try {
    await ExternalLoyaltyAPI.refundPointsReservation({
      gatewayUrl: Endpoints.PLATFORM_GATEWAY_URL,
      merchantId: buildMerchantId(companyId, locationId),
      customerId,
      reservationId,
    })

    return {}
  } catch (error) {
    return {
      errorMessage:
        parseErrorMsg(error) || 'Failed to release the loyalty points reserved for this reward',
    }
  }
}

interface MerchantStores {
  currentCompanyStore?: CurrentCompanyStore
  posStore?: POSStore
}

interface RewardReservationStores extends MerchantStores {
  payment: Payments.CreatePaymentV2
}

const getMerchantContext = ({ currentCompanyStore, posStore }: MerchantStores) => {
  const companyId = currentCompanyStore?.company?.id
  const locationId = posStore?.location?.id

  if (companyId === undefined || locationId === undefined) {
    return undefined
  }

  return { companyId, locationId }
}

/**
 * Reserving or releasing a reward moves the balance Growth holds, so the profile has to be read
 * again for the customer bar to show the new number. The store owns that read, so publishing it
 * there is what refreshes the badge; the balance is also mirrored onto the payment because the
 * redemption maths reads `customer.creditBalance`.
 */
export const maybeRefreshCustomerLoyaltyPoints = async ({
  payment,
  currentCompanyStore,
}: RewardReservationStores): Promise<void> => {
  const customerWithHistory = payment.customerWithHistory

  if (!customerWithHistory || !currentCompanyStore?.isGrowthLoyaltyActive) {
    return
  }

  const result = await currentCompanyStore.loadSaleCustomerLoyalty(customerWithHistory.customer)

  if (result.status !== 'enrolled' || result.points === null) {
    return
  }

  payment.addCustomer({
    ...customerWithHistory,
    customer: {
      ...customerWithHistory.customer,
      creditBalance: result.points,
    },
  })
}

export const maybeReserveRewardPoints = async ({
  externalDiscount,
  ...stores
}: RewardReservationStores & {
  externalDiscount?: ExternalLoyaltyModels.IRewardContract
}): Promise<{ reservationId?: string; errorMessage?: string }> => {
  if (!externalDiscount || !stores.currentCompanyStore?.isGrowthLoyaltyActive) {
    return {}
  }

  const merchant = getMerchantContext(stores)

  if (!merchant) {
    return {}
  }

  const result = await reserveRewardPoints({
    payment: stores.payment,
    ...merchant,
    rewardId: externalDiscount.id,
  })

  if ('errorMessage' in result) {
    return result
  }

  return { reservationId: result.reservationId }
}

const releaseLineReservations = async ({
  customerId,
  lines,
  companyId,
  locationId,
}: {
  customerId?: string | null
  lines: RewardLine[]
  companyId: number
  locationId: number
}): Promise<{ errorMessage?: string }> => {
  if (!customerId) {
    return {}
  }

  let firstErrorMessage: string | undefined

  for (const line of lines) {
    const reservationId = line.externalReward?.reservationId

    if (!reservationId) {
      continue
    }

    const { errorMessage } = await maybeRefundRewardReservation({
      customerId,
      companyId,
      locationId,
      reservationId,
    })

    if (errorMessage) {
      firstErrorMessage = firstErrorMessage || errorMessage
    } else {
      line.externalReward = undefined
    }
  }

  return firstErrorMessage ? { errorMessage: firstErrorMessage } : {}
}

export const maybeReleaseReservedRewardPoints = async ({
  lines,
  ...stores
}: RewardReservationStores & { lines: RewardLine[] }): Promise<{ errorMessage?: string }> => {
  const merchant = getMerchantContext(stores)

  if (!merchant) {
    return {}
  }

  return releaseLineReservations({
    customerId: stores.payment.customer?.id,
    lines,
    ...merchant,
  })
}

export const maybeReleasePaymentQueueRewardPoints = async ({
  paymentQueue,
  companyId,
}: {
  paymentQueue: ReservedPaymentQueue
  companyId: number
}): Promise<{ errorMessage?: string }> =>
  releaseLineReservations({
    customerId: paymentQueue.customerId,
    // The queue list endpoints leave the lines out, so a queue can reach here without them.
    lines: paymentQueue.paymentLines ?? [],
    companyId,
    locationId: paymentQueue.locationId,
  })
